import { useCallback, useEffect, useState } from 'react';
import { http } from '../http';
import { useAuth0 } from 'Auth';
import type { AttributeTypeKey } from 'BlockList/types';

/**
 * GET /clearcover_admin/blocklist/attribute_types
 *
 * Per `block_list_feature_openapi.yaml` (#/paths/~1attribute_types):
 *   200 → { data: AttributeTypeMetadata[] }
 *   AttributeTypeMetadata = {
 *     attribute_type: AttributeType,
 *     attribute_values: string[]   // expected keys for the values hash
 *   }
 *
 * Drives the AddAttributeModal's type dropdown so the available set is
 * sourced from the spec, not hard-coded on the client.
 */

export type AttributeTypeMetadata = {
  attributeType: AttributeTypeKey;
  attributeValueKeys: string[];
};

type ServerRow = {
  attribute_type: AttributeTypeKey;
  attribute_values: string[];
};

type ServerResponse = {
  data: ServerRow[];
};

const transformRow = (row: ServerRow): AttributeTypeMetadata => ({
  attributeType: row.attribute_type,
  attributeValueKeys: row.attribute_values,
});

export const getAttributeTypes = (token: string) => {
  return http.get<AttributeTypeMetadata[]>({
    url: '/clearcover_admin/blocklist/attribute_types',
    token,
    transformResponse: (
      jsonData: string | ServerResponse
    ): AttributeTypeMetadata[] => {
      const raw: ServerResponse =
        typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      return raw.data.map(transformRow);
    },
  });
};

export type UseAttributeTypesResult = {
  data: AttributeTypeMetadata[];
  loading: boolean;
  error: string | null;
};

export const useAttributeTypes = (
  enabled: boolean = true
): UseAttributeTypesResult => {
  const { getToken } = useAuth0();
  const [data, setData] = useState<AttributeTypeMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await getAttributeTypes(token);
      if (response && response.status === 200 && response.data) {
        setData(response.data);
      } else {
        setError('Failed to load attribute types.');
      }
    } catch {
      setError('Failed to load attribute types.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, load]);

  return { data, loading, error };
};
